import { Action, ActionReducer, createAction } from '@ngrx/store';

export const LOGOUT = createAction('[Account Logout] Logout user');

// State slices that survive logout. Everything else is wiped (reset to its
// reducer's initial state) so no user/session data leaks across logins.
//
// `appSettings` holds tenant white-label branding (theme/logo/landing image),
// not user data. It is fetched only once, at app startup, so wiping it here
// left a 2nd login in the same app session with `theme === null` — dropping the
// white-label colors from the hosted SSO login page, which reads the theme from
// the store synchronously to build the authorize URL (ART-19605). Keeping it is
// safe: it contains no user data.
//
// The branding is per-install (NativeScript builds are single-tenant: a fixed
// environment.customerIdent, no runtime tenant switch). If a multi-tenant flow
// is ever added, its customerIdent change dispatches LoadAppSettings, which
// overwrites this slice for the new tenant — so the preserved value self-heals.
// The appSettingsReducer resets appSettingsActionState on LOGOUT (see its
// on(LOGOUT) handler) so a resolver-gated refetch is never suppressed by a
// stale `done` flag carried across logout.
const PRESERVED_KEYS = ['appSettings'] as const;

export function clearState<State>(reducer: ActionReducer<State>): ActionReducer<State> {
  return function clearStateFn(state: State, action: Action) {
    if (action.type === LOGOUT.type) {
      // Drop every slice except the preserved ones. The preserved slices keep
      // their current value; every other slice reducer gets `undefined` and
      // re-initialises to its own initial state.
      const current = state as Record<string, unknown> | undefined;
      const preserved: Record<string, unknown> = {};
      for (const key of PRESERVED_KEYS) {
        preserved[key] = current?.[key];
      }
      state = preserved as State;
    }
    return reducer(state, action);
  };
}
